// Pure helpers for the day screen. Separate from the components so the numbers
// can be pinned without mounting anything.
import { MEALS, meal as mealField } from "@common/schema/entry/fields";

export { MEALS };

export const MEAL_LABELS = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snacks",
};

// One group per meal shown, in the order a day is eaten, EVERY one present — an
// empty lunch is a heading with an Add link under it, which is where the next
// thing gets logged. Within a meal, the order things were logged in.
//
// `shown` is the settings' meals. A hidden meal is left out, EXCEPT on a day
// that already has something logged to it: the day's totals count that entry,
// and a total made of food the screen does not list is a number nobody can
// check. Nothing new lands there — neither the forms nor the chat offer it.
export const byMeal = (entries = [], shown = MEALS) =>
  MEALS.map((meal) => {
    const items = entries
      .filter((e) => e.meal === meal)
      .sort((a, b) => String(a.createdAt).localeCompare(String(b.createdAt)));
    const calories = items.reduce((acc, e) => acc + Number(e.calories || 0), 0);
    return { meal, label: MEAL_LABELS[meal], items, calories: Math.round(calories), hidden: !shown.includes(meal) };
  }).filter((group) => !group.hidden || group.items.length);

// The meal picker's options: the meals shown, plus `current` when it is a
// hidden one, so editing an entry logged before its meal was turned off shows
// what it is rather than a blank select. Labelled as the field's own enum is,
// "Snack" rather than the heading's "Snacks".
export const mealOptions = (shown = MEALS, current) =>
  MEALS.filter((meal) => shown.includes(meal) || meal === current).map((meal) => ({
    value: meal,
    label: mealField.enumLabels[MEALS.indexOf(meal)].label,
  }));

// Energy from each macro, as a share of the energy the macros account for —
// 4 kcal/g protein and carbs, 9 kcal/g fat, 2 kcal/g fibre. Fibre is a part of
// carbs (@common/schema/entry/fields), so it is carved out of them rather than
// counted twice: carbs' share is the carbs that are not fibre. Out of the
// macros' own sum rather than the logged calories, so the shares always add
// to 100 even when a label's calories and its macros disagree, which they
// routinely do.
export const macroSplit = ({ protein = 0, carbs = 0, fat = 0, fiber = 0 } = {}) => {
  const kcal = { protein: protein * 4, carbs: Math.max(0, carbs - fiber) * 4, fat: fat * 9, fiber: fiber * 2 };
  const sum = kcal.protein + kcal.carbs + kcal.fat + kcal.fiber;
  if (!sum) return { protein: 0, carbs: 0, fat: 0, fiber: 0 };
  // Largest remainder, so rounding never makes the bar 99 or 101 wide.
  const exact = Object.entries(kcal).map(([macro, k]) => [macro, (k / sum) * 100]);
  const split = Object.fromEntries(exact.map(([macro, share]) => [macro, Math.floor(share)]));
  let short = 100 - Object.values(split).reduce((a, b) => a + b, 0);
  for (const [macro] of [...exact].sort((a, b) => (b[1] % 1) - (a[1] % 1))) {
    if (short-- <= 0) break;
    split[macro] += 1;
  }
  return split;
};

export const formatNumber = (value, digits = 0) =>
  Number(value || 0).toLocaleString([], { maximumFractionDigits: digits });

// A day's calories against the daily target: how much is left, or how far
// over, and the share of the target eaten for the bar. Null with no target —
// the totals then show what was eaten and nothing to measure it against.
export const progress = (calories = 0, target = null) => {
  if (!(Number(target) > 0)) return null;
  const eaten = Math.round(Number(calories || 0));
  const goal = Math.round(Number(target));
  return {
    target: goal,
    left: Math.max(0, goal - eaten),
    over: Math.max(0, eaten - goal),
    // Capped at 100 for the bar; `over` says the rest.
    percent: Math.min(100, Math.round((eaten / goal) * 100)),
  };
};
